import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Lock, Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function RestablecerPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [exito, setExito] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <Card className="bg-slate-900/80 border-slate-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="font-barlow text-2xl font-bold text-slate-50 mb-3">Enlace Invalido</h2>
            <p className="text-slate-400 text-sm mb-6">El enlace de recuperacion es invalido o ha expirado.</p>
            <Link to="/recuperar-password">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="solicitar-nuevo-btn">
                Solicitar Nuevo Enlace
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 4) { toast.error('La contrasena debe tener al menos 4 caracteres'); return; }
    if (password !== confirmar) { toast.error('Las contrasenas no coinciden'); return; }
    setCargando(true);
    try {
      await api.post('/auth/resetear-password', { token, password });
      setExito(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al restablecer la contrasena');
    } finally { setCargando(false); }
  };

  if (exito) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <Card className="bg-slate-900/80 border-slate-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="font-barlow text-2xl font-bold text-slate-50 mb-3">Contrasena Restablecida</h2>
            <p className="text-slate-400 text-sm mb-6">Tu contrasena ha sido actualizada. Ya puedes iniciar sesion.</p>
            <Link to="/login">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="ir-login-btn">
                Iniciar Sesion
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <Card className="bg-slate-900/80 border-slate-800 max-w-md w-full">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="font-barlow text-2xl font-bold text-slate-50">Nueva Contrasena</h2>
            <p className="text-slate-400 text-sm mt-2">Ingresa tu nueva contrasena</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Nueva contrasena</Label>
              <div className="relative mt-1">
                <Input type={mostrar ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Minimo 4 caracteres"
                  className="bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500 pr-10"
                  data-testid="nueva-password-input" />
                <button type="button" onClick={() => setMostrar(!mostrar)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {mostrar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Confirmar contrasena</Label>
              <Input type={mostrar ? 'text' : 'password'} value={confirmar}
                onChange={e => setConfirmar(e.target.value)}
                placeholder="Repite tu contrasena"
                className={`bg-slate-950 border-slate-700 text-slate-200 focus:border-amber-500 mt-1 ${confirmar && password !== confirmar ? 'border-red-500' : ''}`}
                data-testid="confirmar-nueva-password-input" />
              {confirmar && password !== confirmar && (
                <p className="text-xs text-red-400 mt-1">Las contrasenas no coinciden</p>
              )}
            </div>
            <Button type="submit" disabled={cargando}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm h-11"
              data-testid="restablecer-password-btn">
              {cargando ? 'Procesando...' : 'Restablecer Contrasena'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
